import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Schedule from './Schedule'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'

import {
  Container,
  SubTitle,
  SortButton,
  InfoText,
  LabelRow
} from './styles'

/*
  `field` é a coluna que a API ordena em `GET /schedules?sort=`. Uma chave que ela não conhece não
  dá erro, só deixa a lista na ordem por data — o teste tests/schedule-sort.js da API repete estas
  chaves, então mude lá junto.
*/
const HEADER_COLUMNS = [
  { field: 'customer', label: 'Cliente', detailedOnly: true },
  { field: 'service', label: 'Serviço' },
  { field: 'professional', label: 'Profissional' },
  { field: 'date', label: 'Data' },
  { field: 'time', label: 'Horário' },
  { field: 'created_at', label: 'Criado em', detailedOnly: true },
  { field: 'comission_percentage', label: 'Comissão (%)' },
  { field: 'price', label: 'Preço', style: { justifySelf: 'center' } }
]

/*
  Sem `onSort`, a célula é o título de sempre: assim ficam as telas que não ordenam e as colunas que
  a tela põe em `unsortableFields`.
*/
function HeaderCell({ field, label, style, sort, onSort: handleSort }) {
  if (!handleSort) return <SubTitle style={style}>{label}</SubTitle>

  const active = sort?.field === field
  const SortIcon = !active ? FaSort : (sort.direction === 'desc' ? FaSortDown : FaSortUp)

  return (
    <SortButton as='button' type='button' style={style} onClick={() => handleSort(field)}>
      {label}
      <SortIcon size={12} color={active ? '#252525' : '#A5A5A5'}/>
    </SortButton>
  )
}

export default function ScheduleList ({
  schedules = [],
  totalSchedules,
  detailed = false,
  hasMore,
  endMessage,
  sort,
  unsortableFields = [],
  onSort: handleSort,
  onScroll: handleScroll,
  onScheduleSelected: handleScheduleSelected = () => null,
  ...props
}) {
  const [loading, setLoading] = useState (true)
  const [scheduleStatuses, setScheduleStatuses] = useState ([])
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getScheduleStatuses ()
  }, [schedules])

  const getScheduleStatuses = async () => {
    if (scheduleStatuses.length > 0) return

    try {
      const { data: statuses } = await cutileiApi.get (
        `/schedule_statuses?business_id=${businessId}`, requestConfig
      )

      setScheduleStatuses (statuses)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  if (loading) return <Loading/>

  return (
    <Container {...props}>
      {detailed && totalSchedules !== undefined && totalSchedules > 0 && (
        <SubTitle>
          {totalSchedules + ' ' + (totalSchedules === 1 ? 'Resultado' : 'Resultados')}
        </SubTitle>
      )}
      {schedules.length > 0 ? (
        <>
          <LabelRow detailed={detailed}>
            {HEADER_COLUMNS.filter(column => detailed || !column.detailedOnly).map(column => (
              <HeaderCell
                key={column.field}
                field={column.field}
                label={column.label}
                style={column.style}
                sort={sort}
                onSort={unsortableFields.includes(column.field) ? undefined : handleSort}
              />
            ))}
          </LabelRow>
          <InfiniteScroll
            dataLength={schedules.length}
            next={handleScroll}
            hasMore={hasMore}
            loader={<Loading/>}
            endMessage={endMessage}
            scrollableTarget='customer-modal'
            style={{ overflow: 'visible' }}
          >
            {schedules.map (schedule => (
              <Schedule
                key={schedule.id}
                detailed={detailed}
                data={schedule}
                scheduleStatuses={scheduleStatuses}
                onClick={handleScheduleSelected}
              />
            ))}
          </InfiniteScroll>
        </>
      ) : (
        <InfoText>Nenhum agendamento encontrado</InfoText>
      )}
    </Container>
  )
}
